/*
 * SNUGLER redesign: hero gallery, night sky, sticky add-to-cart bar and cart bubble.
 * Needs snugler-core.js, which must load first.
 */
(() => {
  const SR = window.SnuglerRedesign;
  if (!SR || !SR.register || SR.modules.has('hero')) return;
  const { isReduced, finePointer, clamp, range, lerp, smooth, easeOut, easeBack, fmt, onScroll, whenVisible, store, selectVariant } = SR.util;

  /* ------------------------------------------------------------------ hero gallery */

  function initGallery(root) {
    const gallery = root.querySelector('[data-snx-gallery]');
    if (!gallery || gallery.__snx) return;
    gallery.__snx = true;

    const stage = gallery.querySelector('[data-snx-stage]');
    const slides = Array.from(gallery.querySelectorAll('.snx-gallery__slide'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-snx-thumb]'));
    const counter = gallery.querySelector('[data-snx-gallery-index]');
    const lightbox = root.querySelector('[data-snx-lightbox]');
    let index = 0;

    function show(next, focusSlide = false) {
      if (!slides.length) return;
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const on = i === index;
        slide.classList.toggle('is-active', on);
        slide.tabIndex = on ? 0 : -1;
        slide.setAttribute('aria-hidden', String(!on));
      });
      thumbs.forEach((thumb, i) => {
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: isReduced() ? 'auto' : 'smooth' });
        } else thumb.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = String(index + 1);
      if (focusSlide) slides[index].focus({ preventScroll: true });
    }
    SR.showSlide = show;

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    const prev = gallery.querySelector('[data-snx-gallery-prev]');
    const next = gallery.querySelector('[data-snx-gallery-next]');
    if (prev) prev.addEventListener('click', (e) => (e.stopPropagation(), show(index - 1)));
    if (next) next.addEventListener('click', (e) => (e.stopPropagation(), show(index + 1)));

    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(index - 1, true);
      if (event.key === 'ArrowRight') show(index + 1, true);
    });

    // swipe
    let startX = null;
    let startY = null;
    let swiped = false;
    stage.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
      swiped = false;
    });
    stage.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        show(index + (dx < 0 ? 1 : -1));
      }
      startX = null;
    });

    // pointer parallax
    stage.addEventListener('pointermove', (e) => {
      if (isReduced() || !finePointer.matches) return;
      const rect = stage.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      stage.style.setProperty('--px', `${fmt(x * -14)}px`);
      stage.style.setProperty('--py', `${fmt(y * -14)}px`);
    });
    stage.addEventListener('pointerleave', () => {
      stage.style.setProperty('--px', '0px');
      stage.style.setProperty('--py', '0px');
    });

    // variant image
    document.addEventListener('snx:variant', (event) => {
      const media = event.detail.variant && event.detail.variant.featured_media;
      if (!media) return;
      const target = slides.findIndex((s) => String(s.dataset.mediaId) === String(media.id));
      if (target > -1) show(target);
    });

    // lightbox
    if (lightbox && typeof lightbox.showModal === 'function') {
      const img = lightbox.querySelector('[data-snx-lightbox-img]');
      const count = lightbox.querySelector('[data-snx-lightbox-count]');
      let lbIndex = 0;
      const render = () => {
        const slide = slides[lbIndex];
        img.src = slide.dataset.full;
        img.alt = slide.dataset.alt || '';
        if (count) count.textContent = `${lbIndex + 1} / ${slides.length}`;
        if (!isReduced() && img.animate) img.animate([{ opacity: 0, transform: 'scale(.97)' }, { opacity: 1, transform: 'none' }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
      };
      const step = (d) => {
        lbIndex = (lbIndex + d + slides.length) % slides.length;
        render();
      };
      slides.forEach((slide, i) =>
        slide.addEventListener('click', () => {
          if (swiped) return;
          lbIndex = i;
          render();
          lightbox.showModal();
          document.documentElement.style.overflow = 'hidden';
        })
      );
      lightbox.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        show(lbIndex);
      });
      lightbox.querySelector('[data-snx-lightbox-close]').addEventListener('click', () => lightbox.close());
      lightbox.querySelector('[data-snx-lightbox-prev]').addEventListener('click', () => step(-1));
      lightbox.querySelector('[data-snx-lightbox-next]').addEventListener('click', () => step(1));
      lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') step(-1);
        if (e.key === 'ArrowRight') step(1);
      });
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox || e.target.classList.contains('snx-lightbox__inner')) lightbox.close();
      });
      let lx = null;
      img.addEventListener('pointerdown', (e) => (lx = e.clientX));
      img.addEventListener('pointerup', (e) => {
        if (lx !== null && Math.abs(e.clientX - lx) > 40) step(e.clientX < lx ? 1 : -1);
        lx = null;
      });
    }
  }

  /* ------------------------------------------------------------------ hero night sky */

  function initSky(root) {
    const canvas = root.querySelector('[data-snx-sky]');
    if (!canvas || canvas.__snx || isReduced()) return;
    canvas.__snx = true;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let motes = [];
    let running = false;
    let raf = 0;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(clamp((width * height) / 26000, 18, 60));
      motes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.6 + Math.random() * 2.2,
        vx: (Math.random() - 0.5) * 0.08,
        vy: -0.04 - Math.random() * 0.12,
        a: 0.25 + Math.random() * 0.5,
        tw: Math.random() * Math.PI * 2,
        warm: Math.random() < 0.18,
      }));
    }

    function frame(t) {
      ctx.clearRect(0, 0, width, height);
      for (const m of motes) {
        m.x += m.vx;
        m.y += m.vy;
        if (m.y < -10) {
          m.y = height + 10;
          m.x = Math.random() * width;
        }
        if (m.x < -10) m.x = width + 10;
        if (m.x > width + 10) m.x = -10;
        const alpha = m.a * (0.65 + 0.35 * Math.sin(t / 1400 + m.tw));
        const g = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, m.r * 4);
        const color = m.warm ? '83,107,122' : '120,145,160';
        g.addColorStop(0, `rgba(${color},${alpha})`);
        g.addColorStop(1, `rgba(${color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.r * 4, 0, Math.PI * 2);
        ctx.fill();
      }
      if (running) raf = requestAnimationFrame(frame);
    }

    function start() {
      if (running || isReduced()) return;
      running = true;
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      cancelAnimationFrame(raf);
    }

    resize();
    window.addEventListener('resize', () => {
      clearTimeout(canvas.__resize);
      canvas.__resize = setTimeout(resize, 150);
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => (entries[0].isIntersecting ? start() : stop())).observe(canvas);
    } else start();
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
  }

  /* ------------------------------------------------------------------ sticky bar */

  function initSticky(root) {
    const bar = root.querySelector('[data-snx-sticky]');
    if (!bar || bar.__snx) return;
    bar.__snx = true;
    const heroForm = root.querySelector('[data-snx-form="hero"]');
    if (!heroForm) return;

    let visible = false;
    function setVisible(on) {
      if (on === visible) return;
      visible = on;
      bar.classList.toggle('is-visible', on);
      if (on) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
    }

    function update() {
      const vh = window.innerHeight;
      const heroRect = heroForm.getBoundingClientRect();
      const pastHero = heroRect.bottom < 0;
      const cta = document.querySelector('[data-snx-form="cta"]');
      const footer = document.querySelector('footer.footer, .footer');
      const ctaVisible = cta ? (() => {
        const r = cta.getBoundingClientRect();
        return r.top < vh && r.bottom > 0;
      })() : false;
      const footerVisible = footer ? footer.getBoundingClientRect().top < vh - 40 : false;
      setVisible(pastHero && !ctaVisible && !footerVisible);
    }

    window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------ cart bubble bounce */

  function bumpCart() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble || isReduced()) return;
    bubble.classList.remove('snx-bump');
    void bubble.offsetWidth;
    bubble.classList.add('snx-bump');
  }

  function initCartBump() {
    if (SR.cartBump) return;
    SR.cartBump = true;
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => setTimeout(bumpCart, 120));
      return;
    }
    const target = document.getElementById('cart-icon-bubble');
    if (target && 'MutationObserver' in window) {
      new MutationObserver(() => bumpCart()).observe(target, { childList: true, subtree: true });
    }
  }

  SR.register('hero', (root) => {
    initGallery(root);
    initSky(root);
    initSticky(root);
    initCartBump(root);
  });
})();
